import { useCallback, useEffect, useRef, useState } from 'react';

const TERMLY_ORIGIN = 'https://app.termly.io';
const MSG_PREFIX = '[iFrameSizer]';

// Height to show until Termly reports the real one, so the page never collapses to a
// zero-height box while the policy loads.
const FALLBACK_HEIGHT = 720;

// Renders a Termly policy as a direct iframe. Termly's own embed-policy.min.js loader does
// nothing more than create this same iframe and size it with iframe-resizer, but re-running
// that cross-origin script on every route change (and twice per mount under
// React.StrictMode in development) surfaced as "Script error." crashes in the dev overlay.
// Talking to the iframe directly has no script to re-run.
//
// The policy page runs the iframe-resizer v4 child. It stays silent until the parent sends
// the init string below, then posts "[iFrameSizer]<id>:<height>:<width>:<type>" on every
// size change. The option values mirror what Termly's loader passes (taggedElement height,
// in-page links on) plus iframe-resizer's own defaults for the rest.
function initMessage(id) {
  return [
    MSG_PREFIX + id,
    8,              // bodyMarginV1
    false,          // sizeWidth
    false,          // log
    32,             // interval
    true,           // enablePublicMethods
    true,           // autoResize
    null,           // bodyMargin
    'taggedElement',// heightCalculationMethod
    null,           // bodyBackground
    null,           // bodyPadding
    0,              // tolerance
    true,           // inPageLinks
    'parent',       // resizeFrom
    'scroll',       // widthCalculationMethod
  ].join(':');
}

export default function TermlyEmbed({ dataId }) {
  const frameRef = useRef(null);
  const [height, setHeight] = useState(FALLBACK_HEIGHT);

  useEffect(() => {
    function onMessage(event) {
      if (event.origin !== TERMLY_ORIGIN || typeof event.data !== 'string') return;
      if (!event.data.startsWith(MSG_PREFIX)) return;
      // Only honour messages from this policy's frame - the cookie/terms/privacy pages
      // share this component and could, briefly, both be mounted during a route change.
      const [id, rawHeight] = event.data.slice(MSG_PREFIX.length).split(':');
      if (id !== dataId) return;
      const next = Math.ceil(Number(rawHeight));
      if (Number.isFinite(next) && next > 0) setHeight(next);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [dataId]);

  // The handshake that makes the policy page start reporting its height.
  const handleLoad = useCallback(() => {
    const win = frameRef.current?.contentWindow;
    if (win) win.postMessage(initMessage(dataId), TERMLY_ORIGIN);
  }, [dataId]);

  const src = `${TERMLY_ORIGIN}/policy-viewer/iframe-content.html?policyUUID=${encodeURIComponent(dataId)}&viewMethod=embedded`;

  return (
    <iframe
      ref={frameRef}
      key={dataId}
      id={dataId}
      title="Policy document"
      src={src}
      onLoad={handleLoad}
      style={{ display: 'block', width: '100%', height: `${height}px`, border: 0, overflow: 'hidden' }}
      scrolling="no"
    />
  );
}
